import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fulfillmentService } from '@/services/fulfillment';
import { reportsService } from '@/services/reports';
import {
  PackageCheck,
  Pill,
  Users,
  Stethoscope,
  ScanLine,
  Search,
  ClipboardList,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader, KpiCard, EmptyState, Spinner } from '@/components/ui';

function QuickAction({
  to,
  icon: Icon,
  title,
  description,
  primary,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={
        primary
          ? 'group flex items-center gap-4 rounded-xl bg-primary-600 p-5 text-white shadow-card transition-colors hover:bg-primary-700'
          : 'card card-interactive group flex items-center gap-4 p-5'
      }
    >
      <div
        className={
          primary
            ? 'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white/15'
            : 'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700'
        }
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className={primary ? 'font-semibold' : 'font-semibold text-gray-900'}>{title}</h2>
        <p className={primary ? 'truncate text-sm text-primary-50/90' : 'truncate text-sm text-gray-500'}>
          {description}
        </p>
      </div>
      <ChevronRight
        className={
          primary
            ? 'h-5 w-5 shrink-0 text-white/70 rtl-flip'
            : 'h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500 rtl-flip'
        }
      />
    </Link>
  );
}

export function PharmacistDashboard() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  const now = new Date();
  const fromDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const toDate = now.toISOString().split('T')[0]!;
  const allTimeFromDate = '2020-01-01';

  const { data: overview } = useQuery({
    queryKey: ['pharmacist-overview', fromDate, toDate],
    queryFn: () => reportsService.getPharmacistOverview({ fromDate, toDate }),
  });

  const { data: allTimeOverview } = useQuery({
    queryKey: ['pharmacist-overview', 'all-time', toDate],
    queryFn: () => reportsService.getPharmacistOverview({ fromDate: allTimeFromDate, toDate }),
  });

  const { data: recentFulfillments, isLoading: recentLoading } = useQuery({
    queryKey: ['fulfillments', 'recent'],
    queryFn: () => fulfillmentService.getHistory({ limit: 8 }),
  });

  const allTime = (count: number | undefined) =>
    count != null ? t('reports.allTimeTotal', { count }) : undefined;

  return (
    <div>
      <PageHeader title={t('nav.dashboard')} description={t('pharmacist.dashboard.description')} />

      {/* Quick actions: scanning is the primary counter workflow */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <QuickAction
          to="/pharmacist/scan"
          icon={ScanLine}
          title={t('nav.scanQR')}
          description={t('fulfillment.scanInstruction')}
          primary
        />
        <QuickAction
          to="/pharmacist/search"
          icon={Search}
          title={t('nav.search')}
          description={t('pharmacist.dashboard.searchHint')}
        />
        <QuickAction
          to="/pharmacist/history"
          icon={ClipboardList}
          title={t('nav.history')}
          description={t('pharmacist.dashboard.fulfillmentsCount', {
            count: recentFulfillments?.pagination.total ?? 0,
          })}
        />
      </div>

      {/* This month */}
      <h2 className="section-title mb-3">{t('reports.thisMonth')}</h2>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t('reports.totalFulfillments')}
          value={overview?.totalFulfillments ?? '—'}
          hint={allTime(allTimeOverview?.totalFulfillments)}
          icon={PackageCheck}
          tone="success"
          to="/pharmacist/history"
        />
        <KpiCard
          label={t('reports.itemsDispensed')}
          value={overview?.totalItemsDispensed ?? '—'}
          hint={allTime(allTimeOverview?.totalItemsDispensed)}
          icon={Pill}
          tone="primary"
          to="/pharmacist/reports"
        />
        <KpiCard
          label={t('reports.uniquePatients')}
          value={overview?.uniquePatients ?? '—'}
          hint={allTime(allTimeOverview?.uniquePatients)}
          icon={Users}
          tone="navy"
          to="/pharmacist/reports"
        />
        <KpiCard
          label={t('reports.uniquePrescribers')}
          value={overview?.uniquePrescribers ?? '—'}
          hint={allTime(allTimeOverview?.uniquePrescribers)}
          icon={Stethoscope}
          tone="warning"
          to="/pharmacist/reports"
        />
      </div>

      {/* Recent fulfillments */}
      <div className="card p-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="card-title">{t('pharmacist.dashboard.recentActivity')}</h2>
          <Link to="/pharmacist/history" className="link text-sm">
            {t('common.seeAll')}
          </Link>
        </div>

        {recentLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6" />
          </div>
        ) : recentFulfillments?.data && recentFulfillments.data.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {recentFulfillments.data.map((fulfillment) => (
              <li key={fulfillment.id}>
                <Link
                  to={`/pharmacist/prescription/${fulfillment.prescriptionId}`}
                  className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-gray-50"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-50 text-success-700">
                    <PackageCheck className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">
                      {t('pharmacist.common.rxNumber', { id: fulfillment.prescriptionId.slice(-8) })}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(fulfillment.fulfilledAt).toLocaleString(dateLocale, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                  {fulfillment.partialFulfillment ? (
                    <span className="badge-warning">{t('fulfillment.partiallyFulfilled')}</span>
                  ) : (
                    <span className="badge-success">{t('fulfillment.fulfilled')}</span>
                  )}
                  <ChevronRight className="hidden h-4 w-4 shrink-0 text-gray-300 group-hover:text-gray-500 sm:block rtl-flip" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={ClipboardList}
            title={t('pharmacist.history.empty')}
            description={t('pharmacist.history.emptyHint')}
          />
        )}
      </div>
    </div>
  );
}
